import { useMemo } from "react";
import { cn } from "@/lib/utils";

/**
 * Lightweight isometric drawing of a rig design (no WebGL). Input is the compact `rig_preview` payload from
 * /api/map/overview: boxes = [x, z, sx, sz, floor0, floors, color, kind] in metres.
 */
const COS30 = Math.cos(Math.PI / 6);
const SIN30 = 0.5;

const shade = (hex, f) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "#d8d2c4");
  if (!m) return hex;
  const c = m.slice(1).map((h) => Math.max(0, Math.min(255, Math.round(parseInt(h, 16) * f))));
  return `#${c.map((v) => v.toString(16).padStart(2, "0")).join("")}`;
};

export const RigIsoPreview = ({ preview, className, height = 96, testId = "rig-iso-preview" }) => {
  const { paths, viewBox } = useMemo(() => {
    if (!preview?.boxes?.length) return { paths: [], viewBox: "0 0 1 1" };
    const fh = preview.floor_height || 3;
    const P = (x, z, y) => [(x - z) * COS30, (x + z) * SIN30 - y];
    const items = preview.boxes
      .map(([x, z, sx, sz, f0, nf, color, kind]) => ({ x, z, sx, sz, y0: f0 * fh, y1: (f0 + nf) * fh, color: color || "#d8d2c4", kind }))
      .sort((a, b) => a.x + a.z - (b.x + b.z) || a.y0 - b.y0);
    const out = [];
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    const push = (pts, fill, stroke) => {
      for (const [px, py] of pts) {
        minX = Math.min(minX, px); maxX = Math.max(maxX, px);
        minY = Math.min(minY, py); maxY = Math.max(maxY, py);
      }
      out.push({ d: `M${pts.map((p) => `${p[0].toFixed(2)},${p[1].toFixed(2)}`).join("L")}Z`, fill, stroke });
    };
    for (const b of items) {
      const { x, z, sx, sz, y0, y1, color } = b;
      // faces visible from the +x/+z corner: top, front (z+sz) and right (x+sx)
      push([P(x, z + sz, y0), P(x + sx, z + sz, y0), P(x + sx, z + sz, y1), P(x, z + sz, y1)], shade(color, 0.86), shade(color, 0.7));
      push([P(x + sx, z + sz, y0), P(x + sx, z, y0), P(x + sx, z, y1), P(x + sx, z + sz, y1)], shade(color, 0.74), shade(color, 0.62));
      push([P(x, z, y1), P(x + sx, z, y1), P(x + sx, z + sz, y1), P(x, z + sz, y1)], shade(color, 1.04), shade(color, 0.72));
    }
    const pad = 1.2;
    return { paths: out, viewBox: `${(minX - pad).toFixed(2)} ${(minY - pad).toFixed(2)} ${(maxX - minX + pad * 2).toFixed(2)} ${(maxY - minY + pad * 2).toFixed(2)}` };
  }, [preview]);

  if (!paths.length) return null;
  return (
    <svg viewBox={viewBox} className={cn("block w-full", className)} style={{ height }} preserveAspectRatio="xMidYMid meet" role="img" aria-label="Isometrisk riggforhåndsvisning" data-testid={testId}>
      {paths.map((p, i) => (
        <path key={i} d={p.d} fill={p.fill} stroke={p.stroke} strokeWidth={0.08} strokeLinejoin="round" />
      ))}
    </svg>
  );
};
